import { useQuery } from '@tanstack/vue-query'
import type { GenericApiError, ReferralError } from '~/types/apiError'
import type {
  Referral,
  ReferralForm,
  ReferralProgram,
  ReferralFriend,
  CanRefer,
  ReferralFormUnauthenticated
} from '~/types/referral'

export const useStoreReferral = defineStore('referral', () => {
  const canRefer = async (email: string): Promise<CanRefer | GenericApiError> => {
    return await useNuxtApp().$api(`/api/customers/referral-referral/can-refer/`, {
      method: 'POST',
      body: { email },
      onResponseError({ response }) {
        throw response._data as GenericApiError
      }
    })
  }

  const sendReferral = async (referralForm: ReferralForm): Promise<unknown | GenericApiError> => {
    const res = await useNuxtApp().$api(`/api/customers/referral-referral/send-email/`, {
      method: 'POST',
      body: { email: referralForm.email },
      onResponseError({ response }) {
        throw response._data as GenericApiError
      }
    })
    return res
  }

  const sendReferralUnauthenticated = async (
    referralForm: ReferralFormUnauthenticated
  ): Promise<unknown | GenericApiError> => {
    const res = await useNuxtApp().$api(`/api/customers/referral-referral/send-email-unauthenticated/`, {
      method: 'POST',
      body: { email: referralForm.email, customer_email: referralForm.customerEmail },
      onResponseError({ response }) {
        throw response._data as GenericApiError
      }
    })
    return res
  }

  const getReferralProgram = () => {
    return useQuery({
      queryKey: ['getReferralProgram'],
      queryFn: async () => {
        return (await useNuxtApp().$api(`/api/customers/referral-program/`)) as ReferralProgram
      },
      enabled: () => useStoreAuth().getJwtToken() !== null,
      staleTime: 1800000 // 30 minutes
    })
  }

  const getReferrals = () => {
    return useQuery({
      queryKey: ['listReferrals'],
      queryFn: async () => {
        return (await useNuxtApp().$api(`/api/customers/referral-referral/`)) as Referral[]
      },
      staleTime: 1800000 // 30 minutes
    })
  }

  const getFriendReferral = async (referralCode: string, email?: string, optinReminder: boolean = false) => {
    const res = await useNuxtApp().$api(`/api/customers/referral-referral/`, {
      method: 'POST',
      body: { referral_code: referralCode, godson_email: email, godson_optin_reminder: optinReminder },
      onResponseError({ response }) {
        throw response._data as ReferralError
      }
    })
    return res as ReferralFriend
  }

  const updateReferral = async (referralId: string, email: string, optinReminder: boolean) => {
    const res = await useNuxtApp().$api(`/api/customers/referral-referral/${referralId}/`, {
      method: 'PATCH',
      body: { godson_email: email, godson_optin_reminder: optinReminder },
      onResponseError({ response }) {
        throw response._data as ReferralError
      }
    })
    return res as ReferralFriend
  }

  return {
    canRefer,
    sendReferral,
    sendReferralUnauthenticated,
    getReferrals,
    getReferralProgram,
    getFriendReferral,
    updateReferral
  }
})
